import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Clock, Pencil, Filter, AlertTriangle } from "lucide-react";
import { fmtDateTime, isoToMTInput, mtInputToISO } from "@/lib/datetime";

function EditEntryDialog({ open, onOpenChange, entry, onSaved }) {
  const [clockIn, setClockIn] = useState("");
  const [clockOut, setClockOut] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && entry) {
      setClockIn(isoToMTInput(entry.clock_in));
      setClockOut(isoToMTInput(entry.clock_out));
      setNote("");
    }
  }, [open, entry]);

  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim()) {
      toast.error("An edit note is required");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/time-entries/${entry.id}`, {
        clock_in: mtInputToISO(clockIn),
        clock_out: clockOut ? mtInputToISO(clockOut) : null,
        edit_note: note.trim(),
      });
      toast.success("Time entry updated");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit time entry</DialogTitle>
          <DialogDescription>
            Adjust the clock-in/out times. An edit note is required and recorded.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="edit-time-form">
          <div className="space-y-2">
            <Label htmlFor="ci">Clock in</Label>
            <Input
              id="ci"
              data-testid="edit-clockin-input"
              type="datetime-local"
              className="h-11"
              value={clockIn}
              onChange={(e) => setClockIn(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="co">Clock out</Label>
            <Input
              id="co"
              data-testid="edit-clockout-input"
              type="datetime-local"
              className="h-11"
              value={clockOut}
              onChange={(e) => setClockOut(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Leave blank to keep the entry open.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="note">Edit note (required)</Label>
            <Textarea
              id="note"
              data-testid="edit-note-input"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for this adjustment"
              required
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="h-11" data-testid="edit-time-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TimeEntries() {
  const [teachers, setTeachers] = useState([]);
  const [data, setData] = useState({ entries: [], totals: [] });
  const [loading, setLoading] = useState(true);
  const [teacherId, setTeacherId] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [editing, setEditing] = useState(null);

  const loadTeachers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/teachers");
      setTeachers(data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (teacherId !== "all") params.teacher_id = teacherId;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/admin/time-entries", { params });
      setData(data);
    } catch (e) {
      toast.error("Failed to load time entries");
    } finally {
      setLoading(false);
    }
  }, [teacherId, start, end]);

  useEffect(() => {
    loadTeachers();
  }, [loadTeachers]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="max-w-6xl" data-testid="time-entries-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
        Time Entries
      </h1>
      <p className="mt-1 text-muted-foreground">View and adjust teacher working hours.</p>

      {/* Filters */}
      <div className="mt-6 bg-card border border-border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1">
            <Filter className="h-3 w-3" /> Teacher
          </Label>
          <Select value={teacherId} onValueChange={setTeacherId}>
            <SelectTrigger className="h-11 w-52" data-testid="filter-teacher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teachers</SelectItem>
              {teachers.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">From</Label>
          <Input
            type="date"
            className="h-11"
            data-testid="filter-start"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To</Label>
          <Input
            type="date"
            className="h-11"
            data-testid="filter-end"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        {(start || end || teacherId !== "all") && (
          <Button
            variant="ghost"
            className="h-11"
            data-testid="filter-clear"
            onClick={() => {
              setTeacherId("all");
              setStart("");
              setEnd("");
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Totals */}
      {data.totals.length > 0 && (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="totals-panel">
          {data.totals.map((t) => (
            <div key={t.teacher_id} className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">{t.teacher_name}</p>
              <p className="font-heading text-2xl font-medium text-foreground">
                {t.total_hours} <span className="text-base text-muted-foreground">hrs</span>
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="mt-4 bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : data.entries.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="time-entries-empty">
            <Clock className="h-8 w-8 mx-auto mb-3 opacity-40" />
            No time entries for the selected filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Teacher</TableHead>
                  <TableHead>Clock in</TableHead>
                  <TableHead>Clock out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Edited</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.map((e) => {
                  const overHours = e.total_hours != null && e.total_hours > 10;
                  return (
                  <TableRow
                    key={e.id}
                    data-testid={`time-row-${e.id}`}
                    className={overHours ? "bg-destructive/5" : undefined}
                  >
                    <TableCell className="font-medium">{e.teacher_name}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDateTime(e.clock_in)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {e.clock_out ? (
                        <span className="inline-flex items-center gap-2">
                          {fmtDateTime(e.clock_out)}
                          {e.auto_closed && (
                            <Badge
                              variant="outline"
                              className="border-[hsl(var(--gold))] text-[hsl(var(--gold))]"
                              title="Closed automatically after 8 hours — verify and adjust if needed"
                              data-testid={`auto-closed-badge-${e.id}`}
                            >
                              Auto-closed
                            </Badge>
                          )}
                        </span>
                      ) : (
                        <Badge className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))] text-[#1C2321]">Open</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {e.total_hours != null ? (
                        <span className="inline-flex items-center gap-2">
                          {`${e.total_hours}h`}
                          {overHours && (
                            <Badge
                              variant="outline"
                              className="border-destructive text-destructive gap-1"
                              title="Over 10 hours — please verify the sign-in/sign-out times"
                              data-testid={`over-hours-badge-${e.id}`}
                            >
                              <AlertTriangle className="h-3 w-3" /> 10h+
                            </Badge>
                          )}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {e.edited_by_admin_id ? (
                        <Badge variant="secondary" title={e.edit_note || ""}>Edited</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(e)}
                        data-testid={`edit-time-${e.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-1.5" /> Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <EditEntryDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        entry={editing}
        onSaved={load}
      />
    </div>
  );
}
